/**
 * City Autocomplete Helper for Alpine.js
 * Wraps EasyAutocomplete with genCityAcOptions for trip/profile city fields
 */

/**
 * Initialize city autocomplete on an input element for use with Alpine.js
 * @param {HTMLElement} inputElement - The input element to initialize
 * @param {Function} onSelect - Callback when a city is selected. Receives: { id, name, countryIso }
 * @param {Object} options - Optional configuration
 * @param {number} options.minCharNumber - Minimum characters before searching (default: 1)
 * @returns {boolean} True if initialized successfully, false otherwise
 */
export function initCityAutocomplete(inputElement, onSelect, options = {}) {
    if (!inputElement) {
        return false;
    }

    const $input = $(inputElement);

    // Check if already initialized
    if ($input.data('easyAutocomplete')) {
        return true;
    }

    // Check if EasyAutocomplete is available
    if (typeof $input.easyAutocomplete !== 'function') {
        console.error('[city-autocomplete] EasyAutocomplete not available');
        return false;
    }

    // Check if genCityAcOptions is available (from jq-common.js)
    if (typeof window.genCityAcOptions !== 'function') {
        console.error('[city-autocomplete] genCityAcOptions not available');
        return false;
    }

    // Get the base options from genCityAcOptions
    const acOptions = window.genCityAcOptions($input, {
        minCharNumber: options.minCharNumber || 1
    });

    // Override the list.onChooseEvent to capture selection
    const originalOnChoose = acOptions.list?.onChooseEvent;
    acOptions.list = acOptions.list || {};
    acOptions.list.onChooseEvent = function() {
        // Get the selected item data from EasyAutocomplete
        const selectedItem = $input.getSelectedItemData();
        if (selectedItem && selectedItem.id) {
            const cityData = {
                id: selectedItem.id,
                name: $input.val(),
                countryIso: (selectedItem.country_iso || '').toLowerCase()
            };

            // Call the provided callback
            if (typeof onSelect === 'function') {
                onSelect(cityData);
            }
        }

        // Call original handler if exists
        if (originalOnChoose) {
            originalOnChoose.call(this);
        }

        // Close dropdown and dismiss keyboard on mobile after selection
        const container = $input.next('.easy-autocomplete-container');
        container.find('ul').hide();
        inputElement.blur();
    };

    // Initialize autocomplete
    $input.easyAutocomplete(acOptions);
    return true;
}

/**
 * Create a city autocomplete initializer method for Alpine components
 * This returns a method that can be called within an Alpine component context
 * @param {string} refName - The x-ref name of the input element
 * @param {Function} onSelect - Callback when a city is selected
 * @returns {Function} Method to be used in Alpine component
 */
export function createCityAutocompleteInit(refName, onSelect) {
    return function() {
        const input = this.$refs[refName];
        return initCityAutocomplete(input, onSelect.bind(this));
    };
}
